/**
 * The archive grid: a fit-to-width table on tablet+ and a dense card list on
 * a phone. Both are rendered from the column registry; neither ever scrolls
 * sideways.
 *
 * - Cells combine for density (`Q2 7:32`, `2nd & 5 · OWN 35`, a toned gain
 *   chip) and every row keeps its success tint.
 * - Columns the width cannot hold are counted in the header ("+N") and shown,
 *   with every other recorded value, in the row expander.
 * - Each row has an actions menu: Edit, Insert play before / after, Delete.
 */
import { Fragment, useRef, useState, type ReactNode } from 'react';

import { StarButton } from '../../components/star-button';
import type { PlayView } from '../../lib/api-types';
import type { LiveStatus } from '../../lib/live-client';
import { PHASE_LABELS, PHASE_SHORT_LABELS } from '../play-entry/play-catalog';

import {
  appliesTo,
  gainText,
  notMeasuredReason,
  isOpponentValue,
  num,
  phaseOf,
  situationText,
  tagLabels,
  timeText,
  playcallText,
  playTypeLabel,
  outcomeText,
  type ArchiveColumn,
  type FilterFacet,
} from './archive-columns';
import type { FittedColumn } from './archive-layout';
import type { ArchiveQuery } from './archive-query';
import { ColumnHeader } from './column-menu';
import { NOT_RECORDED } from './custom-columns';
import { Popover } from './popover';

export type RowAction = 'edit' | 'insert-before' | 'insert-after' | 'delete' | 'details';

export interface RowActionPermissions {
  canDelete: boolean;
  /** Coaching staff, stat operators, Team Admin: star / unstar (team-wide). */
  canFavorite?: boolean;
}

/** The team-wide star in the Play # cell / phone card head. */
function RowStar({
  play,
  permissions,
  onToggleFavorite,
  size = 'sm',
}: {
  play: PlayView;
  permissions: RowActionPermissions;
  onToggleFavorite?: (play: PlayView) => void;
  size?: 'sm' | 'lg';
}): JSX.Element {
  const canToggle = permissions.canFavorite === true && onToggleFavorite !== undefined;
  return (
    <StarButton
      isStarred={play.is_favorited === true}
      playNumber={play.play_number}
      size={size}
      onToggle={canToggle ? () => onToggleFavorite(play) : undefined}
      testId={`play-star-${play.play_number}`}
    />
  );
}

/* ------------------------------------------------------------------ cells */

export function successClass(play: PlayView): string {
  if (play.success_measure === true) return 'arch-row--success';
  if (play.success_measure === false) return 'arch-row--failure';
  return 'arch-row--neutral';
}

function gainTone(play: PlayView): string {
  const gain = num(play.stats.gain_loss);
  if (gain === null) return 'none';
  if (gain > 0) return 'pos';
  if (gain < 0) return 'neg';
  return 'zero';
}

export function ConflictBadge({
  play,
  onOpen,
}: {
  play: PlayView;
  onOpen: () => void;
}): JSX.Element | null {
  if (play.conflict_status !== 'unresolved') return null;
  const count = Math.max(play.version_count, 2);
  return (
    <button
      type="button"
      className="arch-conflict"
      data-testid={`conflict-badge-${play.play_number}`}
      aria-label={`${count} conflicting entries — compare and resolve`}
      title={`Conflict: ${count} submissions for play ${play.play_number} disagree. Open to compare and resolve.`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      ⚠<span className="arch-conflict__n">{count}</span>
    </button>
  );
}

function Cell({ column, play }: { column: ArchiveColumn; play: PlayView }): JSX.Element {
  if (!appliesTo(column, play)) return <span className="arch-na" aria-label="Not on this unit" />;
  switch (column.cell) {
    case 'phase': {
      const phase = phaseOf(play);
      return phase === null ? (
        <span className="arch-muted">{NOT_RECORDED}</span>
      ) : (
        <span className={`arch-unit arch-unit--${phase}`} title={PHASE_LABELS[phase]}>
          {PHASE_SHORT_LABELS[phase]}
        </span>
      );
    }
    case 'gain': {
      const text = gainText(play.stats);
      return <span className={`arch-gain arch-gain--${gainTone(play)}`}>{text}</span>;
    }
    case 'success':
      return play.success_measure === null ? (
        <span className="arch-muted" title={notMeasuredReason(play)}>
          –<span className="visually-hidden">{notMeasuredReason(play)}</span>
        </span>
      ) : (
        <span
          className={`arch-succ arch-succ--${play.success_measure ? 'yes' : 'no'}`}
          title={play.success_measure ? 'Success' : 'Not success'}
        >
          {play.success_measure ? '✓' : '✕'}
          <span className="visually-hidden">
            {play.success_measure ? 'Success' : 'Not success'}
          </span>
        </span>
      );
    case 'tags': {
      const tags = tagLabels(play.stats);
      if (tags.length === 0) return <span className="arch-muted">{NOT_RECORDED}</span>;
      return (
        <span className="arch-tags" title={column.text(play)}>
          {tags.map((tag) => (
            <span key={tag} className="arch-tag">
              {tag}
            </span>
          ))}
        </span>
      );
    }
    case 'catalog': {
      const text = column.text(play);
      if (text === NOT_RECORDED) return <span className="arch-muted">{NOT_RECORDED}</span>;
      const isOpp = isOpponentValue(column, play);
      const isNone = isNoneValue(play, column);
      return (
        <span
          className={
            [isOpp ? 'arch-opp' : '', isNone ? 'arch-muted' : ''].filter(Boolean).join(' ') ||
            undefined
          }
          title={
            isOpp
              ? `Opponent’s ${column.title.toLowerCase()} (italic = opponent’s call): ${text}`
              : `${column.title}: ${text}`
          }
        >
          {text}
        </span>
      );
    }
    default: {
      const text = column.text(play);
      return text === NOT_RECORDED ? (
        <span className="arch-muted">{NOT_RECORDED}</span>
      ) : (
        <span title={text}>{text}</span>
      );
    }
  }
}

/** A catalog "none" option (`None` motion / blitz) — recorded, but not news. */
export function isNoneValue(play: PlayView, column: ArchiveColumn): boolean {
  const raw = play.stats[column.id];
  return typeof raw === 'string' && /^(none|no[-_ ]?\w*)$/i.test(raw.trim());
}

/* ------------------------------------------------------------ row actions */

export function RowActionsMenu({
  play,
  permissions,
  onAction,
  isLarge = false,
}: {
  play: PlayView;
  permissions: RowActionPermissions;
  onAction: (action: RowAction, play: PlayView) => void;
  isLarge?: boolean;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const items: { action: RowAction; label: string; tone?: 'danger' }[] = [
    { action: 'edit', label: 'Edit play' },
    { action: 'insert-before', label: 'Insert play before' },
    { action: 'insert-after', label: 'Insert play after' },
    { action: 'details', label: 'Details & versions' },
    ...(permissions.canDelete
      ? [{ action: 'delete' as const, label: 'Delete play', tone: 'danger' as const }]
      : []),
  ];
  const run = (action: RowAction): void => {
    setIsOpen(false);
    onAction(action, play);
  };
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`arch-kebab${isLarge ? ' arch-kebab--lg' : ''}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`Actions for play #${play.play_number}`}
        data-testid={`row-actions-${play.play_number}`}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((open) => !open);
        }}
      >
        ⋮
      </button>
      <Popover
        anchorRef={triggerRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        label={`Play #${play.play_number} actions`}
        role="menu"
        className="rowmenu"
        width={210}
        align="end"
      >
        {items.map((item) => (
          <button
            key={item.action}
            type="button"
            role="menuitem"
            className={`rowmenu__item${item.tone === 'danger' ? ' rowmenu__item--danger' : ''}`}
            onClick={() => run(item.action)}
            onKeyDown={(event) => {
              if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
              event.preventDefault();
              const siblings = Array.from(
                event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                  '[role="menuitem"]'
                ) ?? []
              );
              const index = siblings.indexOf(event.currentTarget);
              const next =
                siblings[
                  (index + (event.key === 'ArrowDown' ? 1 : -1) + siblings.length) % siblings.length
                ];
              next?.focus();
            }}
          >
            {item.label}
          </button>
        ))}
      </Popover>
    </>
  );
}

/* --------------------------------------------------------------- expander */

/** Every recorded value the row does not already show, as a dense grid. */
export function PlayDetails({
  play,
  columns,
  shownIds,
  children,
}: {
  play: PlayView;
  columns: readonly ArchiveColumn[];
  shownIds: ReadonlySet<string>;
  children?: ReactNode;
}): JSX.Element {
  const rows = columns.filter(
    (column) => !shownIds.has(column.id) && appliesTo(column, play) && column.id !== 'play_number'
  );
  const recorded = rows.filter((column) => column.text(play) !== NOT_RECORDED);
  const blank = rows.filter((column) => column.text(play) === NOT_RECORDED);
  return (
    <div className="arch-details" data-testid={`play-details-${play.play_number}`}>
      {recorded.length === 0 ? null : (
        <dl className="arch-details__grid">
          {recorded.map((column) => (
            <div key={column.id} className="arch-details__item">
              <dt>
                {isOpponentValue(column, play)
                  ? `Opp. ${column.title.toLowerCase()}`
                  : column.title}
              </dt>
              <dd>{column.text(play)}</dd>
            </div>
          ))}
        </dl>
      )}
      {blank.length === 0 ? null : (
        <p className="arch-details__blank">
          Not recorded: {blank.map((column) => column.title).join(', ')}
        </p>
      )}
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ table */

export interface ArchiveTableProps {
  plays: readonly PlayView[];
  allPlays: readonly PlayView[];
  fitted: readonly FittedColumn[];
  hiddenByWidth: readonly ArchiveColumn[];
  columns: readonly ArchiveColumn[];
  query: ArchiveQuery;
  onQueryChange: (next: ArchiveQuery) => void;
  facets: ReadonlyMap<string, { facet: FilterFacet; column: ArchiveColumn }>;
  expandedId: string | null;
  onToggle: (play: PlayView) => void;
  onAction: (action: RowAction, play: PlayView) => void;
  onHideColumn: (id: string) => void;
  permissions: RowActionPermissions;
  liveStatus: LiveStatus;
  renderExpanded: (play: PlayView) => ReactNode;
  actionsWidth: number;
  density: 'compact' | 'comfortable';
  onShowHidden: () => void;
  /** Star / unstar a play (team-wide). Omitted → stars are read-only. */
  onToggleFavorite?: (play: PlayView) => void;
}

export function ArchiveTable({
  plays,
  allPlays,
  fitted,
  hiddenByWidth,
  columns,
  query,
  onQueryChange,
  facets,
  expandedId,
  onToggle,
  onAction,
  onHideColumn,
  permissions,
  renderExpanded,
  actionsWidth,
  density,
  onShowHidden,
  onToggleFavorite,
}: ArchiveTableProps): JSX.Element {
  const span = fitted.length + 1;
  return (
    <table
      className={`arch-table arch-table--${density}`}
      aria-label="Logged plays"
      data-testid="archive-table"
    >
      <colgroup>
        {fitted.map(({ column, width }) => (
          <col key={column.id} style={{ width: `${width}px` }} />
        ))}
        <col style={{ width: `${actionsWidth}px` }} />
      </colgroup>
      <thead>
        <tr>
          {fitted.map(({ column }) => (
            <th
              key={column.id}
              scope="col"
              className={`arch-th arch-th--${column.align}`}
              data-column={column.id}
              aria-sort={
                query.sort === column.id
                  ? query.dir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : undefined
              }
            >
              <ColumnHeader
                column={column}
                query={query}
                onChange={onQueryChange}
                allPlays={allPlays}
                facets={facets}
                columns={columns}
                onHide={() => onHideColumn(column.id)}
              />
            </th>
          ))}
          <th scope="col" className="arch-th arch-th--actions">
            {hiddenByWidth.length === 0 ? (
              <span className="visually-hidden">Actions</span>
            ) : (
              <button
                type="button"
                className="arch-more"
                title={`No room at this width for: ${hiddenByWidth.map((column) => column.title).join(', ')}. They show when you expand a row — or choose columns.`}
                aria-label={`${hiddenByWidth.length} more columns do not fit — choose columns`}
                data-testid="hidden-columns-count"
                onClick={onShowHidden}
              >
                +{hiddenByWidth.length}
              </button>
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        {plays.map((play) => {
          const isExpanded = expandedId === play.play_id;
          return (
            <Fragment key={play.play_id}>
              <tr
                className={[
                  'arch-row',
                  successClass(play),
                  play.conflict_status === 'unresolved' ? 'arch-row--conflict' : '',
                  isExpanded ? 'arch-row--expanded' : '',
                ]
                  .filter((entry) => entry !== '')
                  .join(' ')}
                data-play={play.play_number}
                onClick={(event) => {
                  const target = event.target as HTMLElement;
                  if (target.closest('button, a, input, select, label') !== null) return;
                  onToggle(play);
                }}
              >
                {fitted.map(({ column }) => (
                  <td
                    key={column.id}
                    className={`arch-td arch-td--${column.align}`}
                    data-column={column.id}
                  >
                    {column.id === 'play_number' ? (
                      <span className="arch-num">
                        <RowStar
                          play={play}
                          permissions={permissions}
                          onToggleFavorite={onToggleFavorite}
                        />
                        <button
                          type="button"
                          className="arch-num__btn"
                          aria-expanded={isExpanded}
                          aria-label={`Play ${play.play_number} — show details`}
                          data-testid={`play-expander-${play.play_number}`}
                          onClick={() => onToggle(play)}
                        >
                          <span className="arch-num__caret" aria-hidden="true">
                            {isExpanded ? '▾' : '▸'}
                          </span>
                          {play.play_number}
                        </button>
                        {(() => {
                          const phase = phaseOf(play);
                          return phase === null ? null : (
                            <span
                              className={`arch-unit arch-unit--${phase}`}
                              title={PHASE_LABELS[phase]}
                            >
                              {PHASE_SHORT_LABELS[phase]}
                            </span>
                          );
                        })()}
                        <ConflictBadge play={play} onOpen={() => onAction('details', play)} />
                      </span>
                    ) : (
                      <Cell column={column} play={play} />
                    )}
                  </td>
                ))}
                <td className="arch-td arch-td--actions">
                  <RowActionsMenu play={play} permissions={permissions} onAction={onAction} />
                </td>
              </tr>
              {isExpanded ? (
                <tr className="arch-expander">
                  <td colSpan={span}>{renderExpanded(play)}</td>
                </tr>
              ) : null}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

/* ------------------------------------------------------------- phone cards */

/** Catalog + custom columns worth a chip on the phone card, in preference order. */
export function PlayCards({
  plays,
  chipColumns,
  expandedId,
  onToggle,
  onAction,
  permissions,
  renderExpanded,
  onToggleFavorite,
}: {
  plays: readonly PlayView[];
  chipColumns: readonly ArchiveColumn[];
  expandedId: string | null;
  onToggle: (play: PlayView) => void;
  onAction: (action: RowAction, play: PlayView) => void;
  permissions: RowActionPermissions;
  renderExpanded: (play: PlayView) => ReactNode;
  onToggleFavorite?: (play: PlayView) => void;
}): JSX.Element {
  return (
    <ol className="arch-cards" aria-label="Logged plays" data-testid="archive-cards">
      {plays.map((play) => {
        const phase = phaseOf(play);
        const isExpanded = expandedId === play.play_id;
        const chips = chipColumns
          .filter((column) => appliesTo(column, play) && !isNoneValue(play, column))
          .map((column) => ({ column, text: column.text(play) }))
          .filter((entry) => entry.text !== NOT_RECORDED);
        const tags = tagLabels(play.stats);
        const call = [
          playTypeLabel(String(play.stats.play_type ?? '')),
          playcallText(play.stats),
          outcomeText(play.stats),
        ].filter((part) => part !== '' && part !== NOT_RECORDED);
        return (
          <li
            key={play.play_id}
            className={[
              'arch-card',
              successClass(play),
              play.conflict_status === 'unresolved' ? 'arch-row--conflict' : '',
            ]
              .filter((entry) => entry !== '')
              .join(' ')}
            data-play={play.play_number}
          >
            <div className="arch-card__head">
              <RowStar
                play={play}
                permissions={permissions}
                onToggleFavorite={onToggleFavorite}
                size="lg"
              />
              <button
                type="button"
                className="arch-card__toggle"
                aria-expanded={isExpanded}
                aria-label={`Play ${play.play_number} — show details`}
                data-testid={`play-expander-${play.play_number}`}
                onClick={() => onToggle(play)}
              >
                <span className="arch-card__num">#{play.play_number}</span>
                {phase === null ? null : (
                  <span className={`arch-unit arch-unit--${phase}`}>
                    {PHASE_SHORT_LABELS[phase]}
                  </span>
                )}
                <span className="arch-card__time">{timeText(play.stats)}</span>
                {situationText(play.stats) === NOT_RECORDED ? null : (
                  <span className="arch-card__sit">{situationText(play.stats)}</span>
                )}
              </button>
              <ConflictBadge play={play} onOpen={() => onAction('details', play)} />
              {gainTone(play) === 'none' ? null : (
                <span
                  className={`arch-gain arch-gain--${gainTone(play)}`}
                  title="Gain / loss (yards)"
                >
                  {gainText(play.stats)}
                  <span className="arch-gain__unit">yd</span>
                </span>
              )}
              <RowActionsMenu play={play} permissions={permissions} onAction={onAction} isLarge />
            </div>
            {call.length === 0 ? null : <p className="arch-card__call">{call.join(' · ')}</p>}
            {chips.length === 0 && tags.length === 0 ? null : (
              <p className="arch-card__chips">
                {chips.map(({ column, text }) => (
                  <span
                    key={column.id}
                    className={`arch-chip${isOpponentValue(column, play) ? ' arch-chip--opp' : ''}`}
                    title={
                      isOpponentValue(column, play)
                        ? `Opponent’s ${column.title.toLowerCase()}`
                        : column.title
                    }
                  >
                    <span className="arch-chip__k">
                      {isOpponentValue(column, play)
                        ? `Opp. ${column.header.toLowerCase()}`
                        : column.header}
                    </span>
                    {text}
                  </span>
                ))}
                {tags.map((tag) => (
                  <span key={`tag-${tag}`} className="arch-tag">
                    {tag}
                  </span>
                ))}
              </p>
            )}
            {isExpanded ? renderExpanded(play) : null}
          </li>
        );
      })}
    </ol>
  );
}
